<template>
  <div :class="[is_pinned ? 'relative h-full' : 'flex h-full']">
    <div
      v-if="!is_pinned"
      class="fixed inset-0 z-40"
      @click="SHOW_SIDEBAR(null)"
    />
    <div
      v-if="!mq.isMobile"
      class="z-50 h-full w-[0.10rem] cursor-col-resize transition-colors duration-200 hover:bg-primary-500"
      :class="{ 'bg-red-300': is_resizing, 'absolute top-0 left-0': is_pinned }"
      @mousedown="start_resize"
    />

    <div
      id="sidebar-notifications"
      class="shadow-left z-40 flex h-full bg-white shadow-lg"
      :class="[is_pinned ? '' : 'absolute top-0 right-0 z-50']"
      :style="{ width: mq.isMobile ? '100%' : sidebar_width / 16 + 'rem' }"
    >
      <div
        v-if="!mq.isMobile"
        class="z-50 h-full w-[0.10rem] cursor-col-resize transition-colors duration-200 hover:bg-primary-500"
        :class="{ 'bg-red-300': is_resizing, 'absolute top-0 left-0': is_pinned }"
        @mousedown="start_resize"
      />
      <div class="relative flex w-full flex-col pt-3">
        <SidebarHeader
          class="mb-2"
          :title="$t('notification.title')"
          :is_pinned="is_pinned"
          side="right"
          data_test="notifications"
          @close="SHOW_SIDEBAR(null)"
          @toggle-pin="UPDATE_SIDEBAR_PREFS({ is_pinned: !is_pinned })"
        />

        <div
          v-if="fetch_notifications_at"
          class="absolute inset-0 z-50 flex items-center justify-center bg-white/70"
        >
          <ProgressSpinner />
        </div>

        <NotificationsFilter
          class="mb-2 shrink-0"
          :sort_order="sort_order"
          :sort_order_type="sort_order_type"
          @fetch_notification_subgroups="fetch_notification_subgroups"
          @open_notification_subgroups_all="open_notification_subgroups_all"
          @set_sort_order="
            (new_sort_prefs) => {
              sort_prefs = new_sort_prefs;
            }
          "
        />

        <section
          class="flex-1 shrink grow overflow-y-auto"
          data-test="notification-container"
        >
          <div
            v-if="notification_subgroups_page.length === 0 && !fetch_notifications_at"
            class="mt-5 text-center"
          >
            <p class="text-lg text-gray-500">
              <AppIcon
                v-if="!is_filtering"
                class="mr-2"
                name="regular-thumbs-up"
              />
              {{ $t(is_filtering ? 'notification.labels.noResults' : 'notification.labels.noNewNotification') }}
            </p>
          </div>
          <div
            v-for="(notification_subgroup, index) in notification_subgroups_page"
            :key="notification_subgroup.id"
            :class="{ 'border-b border-gray-100': index < notification_subgroups_page.length - 1 }"
          >
            <NotificationItem
              :notification_subgroup="notification_subgroup"
              @onChangeReadStatus="fetch_notification_subgroups"
              @onDelete="delete_notification_subgroup(notification_subgroup)"
              @click="handle_notification_click"
            />
          </div>
        </section>

        <div
          class="shrink-0"
          @click.stop
        >
          <Pagination
            :pagination="pagination"
            :hideRowsPerPageOptions="false"
            :rowsPerPageOptions="[3, 4, 5, 6, 7, 8, 9, 10, 15, 20, 50, 100]"
            @change="fetchNotificationSubgroupsPage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { NOTIFICATION_SUBGROUPS_PER_PAGE, PAGINATION_STORAGE_KEYS } from '@/app/data/pagination_constants';
import NotificationsFilter from '@/app/shared_components/navbar/notifications/NotificationsFilter.vue';
import NotificationItem from '@/app/shared_components/activities/NotificationItem.vue';
import { differenceInSeconds } from 'date-fns';
import { useMqService } from '@/plugins/mq';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';
import usePagination from '@/composables/usePagination';
import useSidebarResize from '@/composables/useSidebarResize';
import SidebarHeader from '@/app/shared_components/sidebar/SidebarHeader.vue';
import INJECT_KEYS from '@/app/data/inject_keys';

const SORT_ORDER_DEFAULT = 'desc';
const SORT_ORDER_TYPE_DEFAULT = 'updated_at';

const store = useStore();
const route = useRoute();
const mq = useMqService();
const emitter = inject(INJECT_KEYS.EMITTER);

const fetch_notifications_at = ref(null);
const sort_prefs = ref([`${SORT_ORDER_TYPE_DEFAULT} ${SORT_ORDER_DEFAULT}`]);

const { pagination, handlePageChange, dispatchPage, resetPage } = usePagination({
  per_page: NOTIFICATION_SUBGROUPS_PER_PAGE,
  sync_url: false,
  storage_key: PAGINATION_STORAGE_KEYS.NOTIFICATIONS_SIDEBAR,
});

const notification_subgroups_page = computed(() => store.getters.notification_subgroups_page);
const sidebar_type = computed(() => store.getters.sidebar_type);
const sidebar_prefs = computed(() => store.getters.sidebar_prefs);

const is_filtering = computed(() => sidebar_prefs.value.project_id || sidebar_prefs.value.unopen);
const sidebar_width = computed(() => sidebar_prefs.value.sidebar_width);
const is_pinned = computed(() => {
  if (mq.isMobile) {
    return false;
  }
  return sidebar_prefs.value.is_pinned;
});
const is_visible = computed(() => sidebar_type.value === SIDEBAR_TYPE_NOTIFICATIONS);
const sort_order = computed(() => _.last(sort_prefs.value).split(' ')[1]);
const sort_order_type = computed(() => _.last(sort_prefs.value).split(' ')[0]);

const DELETE_NOTIFICATION_SUBGROUP = (payload) => store.dispatch('DELETE_NOTIFICATION_SUBGROUP', payload);
const OPEN_NOTIFICATION_SUBGROUPS = (payload) => store.dispatch('OPEN_NOTIFICATION_SUBGROUPS', payload);
const SHOW_SIDEBAR = (payload) => store.dispatch('SHOW_SIDEBAR', payload);
const UPDATE_NOTIFICATIONS_LAST_OPENED = () => store.dispatch('UPDATE_NOTIFICATIONS_LAST_OPENED');
const UPDATE_SIDEBAR_PREFS = (payload) => store.dispatch('UPDATE_SIDEBAR_PREFS', payload);

const { is_resizing, start_resize } = useSidebarResize({
  sidebar_width,
  direction: 'left',
  onResize: (width) => UPDATE_SIDEBAR_PREFS({ sidebar_width: width }),
});

const fetch_notification_subgroups = async () => {
  if (!is_visible.value) return;
  if (fetch_notifications_at.value && differenceInSeconds(fetch_notifications_at.value, new Date()) > 60)
    fetch_notifications_at.value = null;
  if (fetch_notifications_at.value) return;

  fetch_notifications_at.value = new Date();
  await dispatchPage('FETCH_NOTIFICATION_SUBGROUPS_PAGE');

  // catch current page being out of bounds: reset to first page and fetch again
  if (_.isEmpty(notification_subgroups_page.value) && pagination.page > 1) {
    resetPage();
    await dispatchPage('FETCH_NOTIFICATION_SUBGROUPS_PAGE');
  }
  fetch_notifications_at.value = null;
};

const fetchNotificationSubgroupsPage = async (pagination_next) => {
  if (pagination_next) handlePageChange(pagination_next);
  await fetch_notification_subgroups();
};

const fetch_notification_subgroups_debounce = _.debounce(fetch_notification_subgroups, 1000);

const open_notification_subgroups_all = async () => {
  if (_.isEmpty(notification_subgroups_page.value)) return;
  const ids = _.map(notification_subgroups_page.value, 'id');
  await OPEN_NOTIFICATION_SUBGROUPS({ ids });
  await fetch_notification_subgroups();
};

const delete_notification_subgroup = (notification_subgroup) => {
  DELETE_NOTIFICATION_SUBGROUP({ notification_subgroup });
  fetch_notification_subgroups_debounce();
};

const handle_notification_click = () => {
  if (!is_pinned.value) {
    SHOW_SIDEBAR(null);
  }
};

watch(
  () => route.path,
  () => {
    if (is_visible.value && !mq.isDesktop && !is_pinned.value) {
      SHOW_SIDEBAR(null);
    }
  },
  { immediate: true },
);

watch(is_visible, (val) => {
  if (val) {
    UPDATE_NOTIFICATIONS_LAST_OPENED();
    resetPage();
    fetch_notification_subgroups();
  }
});

watch(sort_prefs, () => {
  UPDATE_SIDEBAR_PREFS({ sort_prefs: sort_prefs.value });
  fetch_notification_subgroups();
});

watch(
  () => pagination.per_page,
  (val) => {
    UPDATE_SIDEBAR_PREFS({ per_page: val });
  },
);

onMounted(() => {
  if (sidebar_prefs.value.sort_prefs) {
    sort_prefs.value = sidebar_prefs.value.sort_prefs;
  } else {
    UPDATE_SIDEBAR_PREFS({ sort_prefs: sort_prefs.value });
  }
  if (sidebar_prefs.value.per_page) {
    pagination.per_page = sidebar_prefs.value.per_page;
  }
  if (emitter) {
    emitter.on('fetchNotificationSubgroups', fetch_notification_subgroups);
  }
});

onBeforeUnmount(() => {
  if (emitter) {
    emitter.off('fetchNotificationSubgroups', fetch_notification_subgroups);
  }
});
</script>
